import { Link, useParams, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { ArrowRight, Clock, MapPin, Calendar, ChevronLeft } from "lucide-react";
import { BLOG_POSTS } from "../data/blogPosts";

function fmtDate(iso) {
    try {
        return new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
            year: "numeric", month: "long", day: "numeric",
        });
    } catch { return iso; }
}

function setMetaDescription(text) {
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) {
        tag = document.createElement("meta");
        tag.name = "description";
        document.head.appendChild(tag);
    }
    tag.content = text;
}

// Article + FAQPage schema so search and AI engines can parse the post.
function buildJsonLd(post) {
    const url = `${window.location.origin}/blog/${post.slug}`;
    const faqs = [];
    const faqStart = post.body.findIndex((b) => b.type === "h2" && /frequently asked/i.test(b.text));
    if (faqStart >= 0) {
        for (let i = faqStart + 1; i < post.body.length && post.body[i].type !== "h2"; i++) {
            const b = post.body[i];
            const next = post.body[i + 1];
            if (b.type === "h3" && next?.type === "p") {
                faqs.push({ "@type": "Question", name: b.text, acceptedAnswer: { "@type": "Answer", text: next.text } });
            }
        }
    }
    const graph = [{
        "@type": "Article",
        headline: post.title,
        description: post.excerpt,
        datePublished: post.published_at,
        dateModified: post.published_at,
        author: { "@type": "Organization", name: post.author },
        publisher: { "@type": "Organization", name: "Aminoplex", url: window.location.origin },
        mainEntityOfPage: url,
        keywords: post.tags.join(", "),
    }];
    if (faqs.length) graph.push({ "@type": "FAQPage", mainEntity: faqs });
    return { "@context": "https://schema.org", "@graph": graph };
}

export function BlogIndex() {
    useEffect(() => {
        document.title = "Research Journal · Aminoplex";
        setMetaDescription("Guidance for qualified laboratories evaluating research-use-only compounds: third-party testing, COAs, and supplier transparency.");
    }, []);
    return (
        <div data-testid="blog-index" className="pt-48 pb-24 bg-[var(--bg)] min-h-screen">
            <div className="max-w-5xl mx-auto px-6 lg:px-10">
                <div className="mb-14">
                    <div className="data-label mb-4 text-[var(--brand)]">RESEARCH JOURNAL</div>
                    <h1 className="font-display text-4xl md:text-6xl leading-[1.05] text-[var(--text-primary)]">
                        Field notes on quality,<br/>testing, and transparency.
                    </h1>
                    <p className="mt-6 text-secondary max-w-2xl leading-relaxed">
                        Guidance for qualified laboratories evaluating research-use-only compounds — written in plain language, grounded in analytical practice.
                    </p>
                </div>

                <div className="space-y-5">
                    {BLOG_POSTS.map((post) => (
                        <Link
                            key={post.slug}
                            to={`/blog/${post.slug}`}
                            data-testid={`blog-card-${post.slug}`}
                            className="group block card p-8 md:p-10 hover:border-[var(--brand)] transition-colors"
                        >
                            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-5 text-[0.68rem] font-ui uppercase tracking-[0.18em] text-[var(--text-muted)]">
                                <span className="inline-flex items-center gap-1.5"><Calendar size={11} />{fmtDate(post.published_at)}</span>
                                <span className="inline-flex items-center gap-1.5"><Clock size={11} />{post.read_minutes} min read</span>
                                <span className="inline-flex items-center gap-1.5"><MapPin size={11} />{post.location}</span>
                            </div>
                            <h2 className="font-display text-2xl md:text-3xl leading-tight text-[var(--text-primary)] group-hover:text-[var(--brand)] transition-colors">
                                {post.title}
                            </h2>
                            <p className="mt-4 text-secondary leading-relaxed">
                                {post.excerpt}
                            </p>
                            <div className="mt-6 inline-flex items-center gap-2 font-ui text-sm text-[var(--brand)] font-semibold uppercase tracking-wide">
                                Read article <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
}

export function BlogPost() {
    const { slug } = useParams();
    const post = BLOG_POSTS.find((p) => p.slug === slug);

    useEffect(() => {
        if (!post) return;
        document.title = `${post.title} · Aminoplex`;
        setMetaDescription(post.excerpt);
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = "blog-jsonld";
        script.text = JSON.stringify(buildJsonLd(post));
        document.head.appendChild(script);
        return () => script.remove();
    }, [post]);

    if (!post) return <Navigate to="/blog" replace />;

    return (
        <article data-testid="blog-post" className="pt-48 pb-32 bg-[var(--bg)] min-h-screen">
            <div className="max-w-3xl mx-auto px-6 lg:px-10">
                <Link to="/blog" className="inline-flex items-center gap-1.5 text-[0.72rem] font-ui uppercase tracking-[0.18em] text-[var(--text-muted)] hover:text-[var(--brand)] transition-colors mb-10" data-testid="back-to-blog">
                    <ChevronLeft size={14} /> Back to Journal
                </Link>

                <header className="mb-12 pb-10 border-b border-[var(--border)]">
                    <div className="data-label mb-5 text-[var(--brand)]">RESEARCH JOURNAL</div>
                    <h1 className="font-display text-3xl md:text-5xl leading-[1.1] text-[var(--text-primary)]">
                        {post.title}
                    </h1>
                    <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-[0.68rem] font-ui uppercase tracking-[0.18em] text-[var(--text-muted)]">
                        <span>By {post.author}</span>
                        <span className="inline-flex items-center gap-1.5"><MapPin size={11} />{post.location}</span>
                        <span className="inline-flex items-center gap-1.5"><Calendar size={11} />{fmtDate(post.published_at)}</span>
                        <span className="inline-flex items-center gap-1.5"><Clock size={11} />{post.read_minutes} min read</span>
                    </div>
                </header>

                <div className="prose-aminoplex">
                    {post.body.map((block, i) => <Block key={i} block={block} />)}
                </div>

                <div className="mt-16 pt-10 border-t border-[var(--border)]">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
                        <div>
                            <div className="data-label mb-2 text-[var(--brand)]">EXPLORE</div>
                            <p className="font-display text-xl text-[var(--text-primary)]">Review our batch-specific COAs and research catalog.</p>
                        </div>
                        <div className="flex gap-3">
                            <Link to="/coa" data-testid="blog-cta-coa" className="btn btn-ghost">COA Library</Link>
                            <Link to="/shop" data-testid="blog-cta-shop" className="btn btn-primary">Shop Products</Link>
                        </div>
                    </div>
                </div>
            </div>
        </article>
    );
}

function Block({ block }) {
    if (block.type === "h2") {
        return <h2 className="font-display text-2xl md:text-3xl text-[var(--text-primary)] mt-14 mb-5 leading-tight">{block.text}</h2>;
    }
    if (block.type === "h3") {
        return <h3 className="font-display text-xl md:text-2xl text-[var(--text-primary)] mt-10 mb-4 leading-tight">{block.text}</h3>;
    }
    if (block.type === "p") {
        return <p className="text-secondary leading-[1.75] mb-5 text-[0.98rem]">{block.text}</p>;
    }
    if (block.type === "ul") {
        return (
            <ul className="space-y-2.5 mb-6 pl-1">
                {block.items.map((it, i) => (
                    <li key={i} className="flex gap-3 text-secondary leading-relaxed text-[0.98rem]">
                        <span className="text-[var(--brand)] mt-[0.4rem]">•</span>
                        <span>{it}</span>
                    </li>
                ))}
            </ul>
        );
    }
    if (block.type === "callout") {
        return (
            <div className="my-8 border-l-2 border-[var(--brand)] pl-5 py-1">
                <p className="font-display text-lg text-[var(--text-primary)] leading-relaxed italic">{block.text}</p>
            </div>
        );
    }
    return null;
}
